// @ts-check

import { makeVar, useReactiveVar } from '@apollo/client'
import { captureException } from '@sentry/react'
import i18n from 'i18next'

/** @typedef {typeof TOAST_TYPE[keyof typeof TOAST_TYPE]} ToastType  */

export const TOAST_TYPE = Object.freeze({
  SUCCESS: 'success',
  INFO: 'info',
  ERROR: 'error',
  CUSTOM: 'custom'
})

/**
 * @typedef {Object} ToastOptions
 * @property {string} header
 * @property {string} body
 * @property {string} [color]
 * @property {JSX.Element} [icon]
 * @property {ToastType} [type]
 * @property {string} [errorId] error id for Sentry
 */

/** @type {import('@apollo/client').ReactiveVar<{key: number, options: ToastOptions}[]>} */
const toastsVar = makeVar([])

const hideToast = (/** @type {number} */ key) => {
  const prevToasts = toastsVar()
  toastsVar(prevToasts.filter((toast) => toast.key !== key))
}

/**
 * Shows a toast with the given options.
 * The toast will automatically hide after 7 seconds.
 * @param {ToastOptions} options
 */
export const showToast = (options) => {
  const prevToasts = toastsVar()

  const now = Date.now()
  toastsVar([...prevToasts, { key: now, options }])

  setTimeout(() => hideToast(now), 7000)
}

/**
 * Reports an error to sentry shows a toast for an unknown exception.
 * @param {unknown} error the caught error
 */
export const captureExceptionWithToast = (error) => {
  const errorId = captureException(error)
  showToast({
    header: i18n.t('common:errorToast.header'),
    body: i18n.t('common:errorToast.body'),
    type: TOAST_TYPE.ERROR,
    errorId
  })
}

export const useToasts = () => {
  return useReactiveVar(toastsVar)
}
